/**
 * Company Table Component
 * Listagem de empresas sobre o `DataTable`: seis colunas, ordenação pedida à
 * API, seleção múltipla e o resumo dos vínculos sob demanda.
 *
 * Sem accordion: o clique na linha abre a ficha, que é onde se cadastra. O que
 * o accordion mostrava (clínicas, responsáveis, contatos e CNAEs) virou a
 * coluna de vínculos, com a contagem na linha e a lista no tooltip.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  FileDownload as ExportIcon,
  MoreVert as MoreIcon,
  ToggleOff as InactivateIcon,
  ToggleOn as ActivateIcon,
} from '@mui/icons-material';

import { pode } from '../../../authorization/pode';
import { useControle } from '../../../authorization/useVeredito';
import { ActionIconButton, ControleIconButton, DataTable } from '../../../components/common';
import type { DataTableColumn, DataTableSort } from '../../../components/common';
import { companyService } from '../../../services/companyService';
import type { Company, CompanySummary } from '../../../types';
import {
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE_LABELS,
  CompanyStatus,
  ModuleType,
  MODULE_LABELS,
} from '../../../types';
import CompanyLinksCell from './CompanyLinksCell';

interface CompanyTableProps {
  companies: Company[];
  totalCount: number;
  /** Página em base 0, como o `DataTable` espera. */
  page: number;
  rowsPerPage: number;
  loading?: boolean;
  sort: DataTableSort;
  /** Quem enxerga só a própria fatia e não tem empresa vinculada. */
  noCompaniesInScope?: boolean;
  /** Filtro aplicado: o vazio fala de filtro, não de cadastro que falta. */
  filtersActive?: boolean;
  onClearFilters?: () => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onExportSelection?: (companies: Company[]) => void;
  onView?: (company: Company) => void;
  /**
   * Quem pode editar e alternar a situação é decidido aqui dentro, ao lado do controle: a
   * lista não recebe mais a resposta pronta, porque o que ela mandava era `undefined`, e o
   * ícone sumia sem dizer nada a quem não podia.
   */
  onEdit: (company: Company) => void;
  onToggleStatus: (company: Company) => void;
}

/** Posições fixas: a comparação entre linhas depende de a sigla não se mover. */
const CONTRACT_MODULES: { module: ModuleType; label: string }[] = [
  { module: ModuleType.MEDICINE, label: 'MED' },
  { module: ModuleType.SAFETY, label: 'SEG' },
  { module: ModuleType.ENVIRONMENT, label: 'AMB' },
  { module: ModuleType.ESOCIAL, label: 'eSO' },
];

/** Cor só quando a situação não é "Ativa" — ver a spec §2. */
const STATUS_COLOR: Partial<Record<CompanyStatus, 'warning' | 'error'>> = {
  [CompanyStatus.SUSPENDED]: 'warning',
  [CompanyStatus.DEFAULTING]: 'error',
  [CompanyStatus.BLOCKED]: 'error',
};

const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

const formatCNPJ = (cnpj: string): string =>
  cnpj.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');

const displayNameOf = (company: Company): string => company.tradeName || company.legalName;

const isCompanyActive = (company: Company): boolean => company.status === CompanyStatus.ACTIVE;

const CompanyTable: React.FC<CompanyTableProps> = ({
  companies,
  totalCount,
  page,
  rowsPerPage,
  loading = false,
  sort,
  noCompaniesInScope = false,
  filtersActive,
  onClearFilters,
  onPageChange,
  onRowsPerPageChange,
  onExportSelection,
  onView,
  onEdit,
  onToggleStatus,
}) => {
  const editar = useControle('/companies/:id/edit', pode('company.update'), 'Editar');
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuCompany, setMenuCompany] = useState<Company | null>(null);
  const [companyToToggle, setCompanyToToggle] = useState<Company | null>(null);

  /**
   * O resumo de cada empresa, guardado enquanto a listagem estiver montada: sair
   * para a ficha desmonta a página e joga o cache fora, que é o que impede o
   * tooltip de mostrar contagem velha depois de a pessoa mexer nos vínculos.
   */
  const summaryCache = useRef(new Map<string, Promise<CompanySummary>>());

  // A seleção vale para as linhas que estão na tela: trocar de página ou de
  // filtro deixaria a barra contando empresas que não estão mais aqui.
  useEffect(() => {
    setSelected([]);
  }, [companies]);

  const loadSummary = useCallback((companyId: string): Promise<CompanySummary> => {
    const cached = summaryCache.current.get(companyId);
    if (cached) return cached;

    const request = companyService.getSummary(companyId).catch((error: unknown) => {
      summaryCache.current.delete(companyId);
      throw error;
    });
    summaryCache.current.set(companyId, request);
    return request;
  }, []);

  const closeMenu = (): void => {
    setMenuAnchor(null);
    setMenuCompany(null);
  };

  const confirmToggle = (): void => {
    if (companyToToggle) {
      // Ativar ou inativar mexe no que a ficha mostra: o resumo guardado deixa
      // de valer para todo mundo na página.
      summaryCache.current.clear();
      onToggleStatus(companyToToggle);
    }
    setCompanyToToggle(null);
  };

  const columns: DataTableColumn<Company>[] = [
    {
      id: 'displayName',
      label: 'Empresa',
      primary: true,
      sortable: true,
      width: '30%',
      truncate: true,
      render: (company) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            title={displayNameOf(company)}
            sx={ELLIPSIS}
          >
            {displayNameOf(company)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, minWidth: 0, alignItems: 'baseline' }}>
            {company.tradeName && company.tradeName !== company.legalName && (
              <Typography
                variant="caption"
                color="text.secondary"
                title={company.legalName}
                sx={ELLIPSIS}
              >
                {company.legalName}
              </Typography>
            )}
            {/* O CNPJ não é truncado: cortado pela metade ele não serve nem para conferir. */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {formatCNPJ(company.cnpj)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'local',
      label: 'Local',
      hideOnMobile: true,
      width: '18%',
      truncate: true,
      render: (company) => {
        const local = company.city ? `${company.city}, ${company.state}` : company.state;
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" title={local} sx={ELLIPSIS}>
              {local}
            </Typography>
            {company.companyType && (
              <Typography variant="caption" color="text.secondary">
                {COMPANY_TYPE_LABELS[company.companyType]}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      id: 'contrato',
      label: 'Contrato',
      hideOnMobile: true,
      render: (company) => (
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {CONTRACT_MODULES.map(({ module, label }) => {
            const contratado = company.modules?.some((m) => m.module === module) ?? false;
            return (
              <Tooltip
                key={module}
                enterTouchDelay={0}
                title={`${MODULE_LABELS[module]} — ${contratado ? 'contratado' : 'não contratado'}`}
              >
                <Typography
                  component="span"
                  variant="caption"
                  tabIndex={0}
                  sx={{
                    color: contratado ? 'text.primary' : 'text.disabled',
                    fontWeight: contratado ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </Tooltip>
            );
          })}
        </Box>
      ),
    },
    {
      id: 'vinculos',
      label: 'Vínculos',
      render: (company) => <CompanyLinksCell company={company} loadSummary={loadSummary} />,
    },
    {
      id: 'situacao',
      label: 'Situação',
      render: (company) => {
        const label = COMPANY_STATUS_LABELS[company.status] ?? company.status;
        const color = STATUS_COLOR[company.status];

        return color ? (
          <Chip label={label} size="small" color={color} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
        );
      },
    },
    {
      id: 'acoes',
      label: 'Ações',
      align: 'right',
      render: (company) => (
        <Box
          sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}
          onClick={(event) => event.stopPropagation()}
        >
          <ControleIconButton controle={editar} size="small" onClick={() => onEdit(company)}>
            <EditIcon fontSize="small" />
          </ControleIconButton>
          <ActionIconButton
            acao="company.update"
            rotulo="Mais ações"
            size="small"
            onClick={(event) => {
              setMenuAnchor(event.currentTarget);
              setMenuCompany(company);
            }}
          >
            <MoreIcon fontSize="small" />
          </ActionIconButton>
        </Box>
      ),
    },
  ];

  const selectedCompanies = companies.filter((company) => selected.includes(company.id));

  // Empresa é a raiz do cadastro: não existe cadeia de pré-requisito antes dela.
  // Quem enxerga uma fatia não pode ler "nada cadastrado" — seria afirmar uma
  // ausência global que essa pessoa não tem como ver. O terceiro vazio, o de
  // filtro sem resultado, é montado pelo próprio EmptyState.
  const emptyState = noCompaniesInScope
    ? {
        title: 'Nenhuma empresa vinculada ao seu acesso',
        description:
          'Você enxerga apenas as empresas às quais foi vinculado. Peça a um administrador para incluir a sua.',
      }
    : {
        title: 'Nenhuma empresa cadastrada ainda',
        description:
          'A empresa é o ponto de partida: funcionários, exames, riscos e agendamentos penduram nela.',
        action: {
          label: 'Cadastrar a primeira empresa',
          node: 'companies',
          deniedHint: 'Peça ao administrador do sistema para cadastrar uma empresa.',
        },
      };

  return (
    <>
      <DataTable<Company>
        columns={columns}
        rows={companies}
        getRowId={(company) => company.id}
        loading={loading}
        emptyState={emptyState}
        filtersActive={filtersActive}
        onClearFilters={onClearFilters}
        onRowClick={onView}
        sort={sort}
        selection={{
          selected,
          onChange: setSelected,
          actions: onExportSelection && (
            <Button
              size="small"
              startIcon={<ExportIcon />}
              onClick={() => onExportSelection(selectedCompanies)}
            >
              Exportar seleção
            </Button>
          ),
        }}
        pagination={{
          page,
          rowsPerPage,
          totalCount,
          onPageChange,
          onRowsPerPageChange,
        }}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setCompanyToToggle(menuCompany);
            closeMenu();
          }}
        >
          {menuCompany && isCompanyActive(menuCompany) ? (
            <>
              <InactivateIcon fontSize="small" sx={{ mr: 1 }} />
              Inativar
            </>
          ) : (
            <>
              <ActivateIcon fontSize="small" sx={{ mr: 1 }} />
              Ativar
            </>
          )}
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(companyToToggle)} onClose={() => setCompanyToToggle(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmar {companyToToggle && isCompanyActive(companyToToggle) ? 'Inativação' : 'Ativação'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja{' '}
            {companyToToggle && isCompanyActive(companyToToggle) ? 'inativar' : 'ativar'} a empresa{' '}
            <strong>{companyToToggle && displayNameOf(companyToToggle)}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {companyToToggle && isCompanyActive(companyToToggle)
              ? 'A empresa será marcada como inativa e não aparecerá nas listagens padrão.'
              : 'A empresa será marcada como ativa e voltará a aparecer nas listagens.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompanyToToggle(null)}>Cancelar</Button>
          <Button
            onClick={confirmToggle}
            color={companyToToggle && isCompanyActive(companyToToggle) ? 'warning' : 'success'}
            variant="contained"
          >
            {companyToToggle && isCompanyActive(companyToToggle) ? 'Inativar' : 'Ativar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyTable;
